import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, EmptyState } from "@/components/bits";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { k: "part_pesan", label: "Part Pesan" },
  { k: "part_datang", label: "Part Datang" },
  { k: "part_komplit", label: "Part Komplit" },
  { k: "installed", label: "Part Terpasang" },
  { k: "overdue", label: "Part Overdue" },
  { k: "part_retur", label: "Part Retur" },
  { k: "warehouse_reserved", label: "WH Reserved" },
  { k: "warehouse_general", label: "WH General Stock" },
  { k: "salvage", label: "Salvage Stock" },
  { k: "unmatched", label: "Unmatched" },
];

const PERIODS = [
  { k: "today", label: "Today" }, { k: "yesterday", label: "Yesterday" },
  { k: "this_week", label: "This Week" }, { k: "last_week", label: "Last Week" },
  { k: "this_month", label: "This Month" },
];

function toCSV(rows) {
  if (!rows.length) return "";
  const cols = Object.keys(rows[0]).filter((c) => typeof rows[0][c] !== "object");
  const head = cols.join(",");
  const body = rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  return head + "\n" + body;
}

export default function Reports() {
  const [type, setType] = useState("part_pesan");
  const [period, setPeriod] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => { setLoading(true); api.get(`/reports?type=${type}&period=${period}`).then((r) => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(load, [type, period]);

  const exportCSV = () => {
    const rows = data?.rows || [];
    if (!rows.length) return toast.error("Tidak ada data");
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `report_${type}_${period}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV diunduh");
  };

  const rows = data?.rows || [];

  return (
    <div>
      <PageHeader title="Reports" subtitle="Filter periode & jenis, lalu export.">
        <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn"><Download size={16} className="mr-2" />Export CSV</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-full sm:w-48" data-testid="report-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map((p) => <SelectItem key={p.k} value={p.k}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Tabs value={type} onValueChange={setType} className="mb-4">
        <TabsList className="flex-wrap h-auto">{TYPES.map((t) => <TabsTrigger key={t.k} value={t.k} data-testid={`report-type-${t.k}`}>{t.label}</TabsTrigger>)}</TabsList>
      </Tabs>

      {loading ? <Spinner /> : rows.length === 0 ? <EmptyState title="Tidak ada data pada periode ini" icon={BarChart3} /> : (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 text-sm text-gray-600">{data.count} baris</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Nopol</th>
                  <th className="text-left px-3 py-2">Merk</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Part Number</th>
                  <th className="text-left px-3 py-2">Nama Part</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">User / Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="border-t border-gray-100">
                    <td className="px-3 py-2 font-mono-data font-semibold">{r.nopol || "—"}</td>
                    <td className="px-3 py-2">{r.vehicle_brand || "—"}</td>
                    <td className="px-3 py-2">{r.vehicle_type || "—"}</td>
                    <td className="px-3 py-2 font-mono-data text-xs">{r.part_number || (r.items ? r.items.map((x) => x.part_number).join(", ") : "—")}</td>
                    <td className="px-3 py-2">{r.part_name || (r.spk_number || r.job_number || "—")}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{(r.created_at || r.date || "").slice(0, 10) || "—"}</td>
                    <td className="px-3 py-2">{r.status ? <StatusBadge status={r.status} /> : (r.match_status ? <StatusBadge status={r.match_status} /> : (r.created_by || "—"))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
